import { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { FullStory } from '@fullstory/browser';

const MenuSearchBar = ({ onSearch, refs, showMenuHeaderAtTopOfPage, additionalOffset = 0, hasBackButton }) => {
    const [query, setQuery] = useState('');
    const [stickyTop, setStickyTop] = useState(0);
    const [isBackButtonShown, setIsBackButtonShown] = useState(false);
    const debounceRef = useRef(null);

    const updateStickyTop = useCallback(() => {
        const navHeight = refs?.current?.navRef?.clientHeight ?? 0;
        const menuHeaderHeight = showMenuHeaderAtTopOfPage ? refs?.current?.headerRef?.clientHeight ?? 0 : 0;
        setStickyTop(navHeight + menuHeaderHeight + additionalOffset);
    }, [refs, showMenuHeaderAtTopOfPage, additionalOffset]);

    const updateBackButtonVisibility = useCallback(() => {
        if (hasBackButton) {
            setIsBackButtonShown(refs?.current?.backButtonRef?.style?.display !== 'none');
        }
    }, [refs, hasBackButton]);

    useLayoutEffect(() => {
        updateStickyTop();
    }, [updateStickyTop]);

    useEffect(() => {
        window.addEventListener('resize', updateStickyTop);
        return () => window.removeEventListener('resize', updateStickyTop);
    }, [updateStickyTop]);

    useEffect(() => {
        if (!hasBackButton) return;
        window.addEventListener('scroll', updateBackButtonVisibility, true);
        return () => window.removeEventListener('scroll', updateBackButtonVisibility, true);
    }, [hasBackButton, updateBackButtonVisibility]);

    const handleChange = useCallback((e) => {
        const value = e.target.value;
        setQuery(value);
        onSearch(value);
        clearTimeout(debounceRef.current);
        if (value) {
            debounceRef.current = setTimeout(() => {
                try {
                    FullStory('trackEvent', {
                        name: 'Menu Searched',
                        properties: { searchText: value.toLowerCase() },
                    });
                } catch {}
            }, 300);
        }
    }, [onSearch]);

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' || e.keyCode === 13) {
            if (document.activeElement === e.target) {
                e.preventDefault(); // avoids weird re-focus behaviour on some devices
                e.target.blur();
            }
        }
    }

    const handleClear = useCallback(() => {
        setQuery('');
        onSearch('');
        clearTimeout(debounceRef.current);
    }, [onSearch]);

    useEffect(() => {
        const pageHandleKeyDown = (e) => {
            if (e.key === 'Escape') handleClear();
        };
        document.addEventListener('keydown', pageHandleKeyDown);
        return () => document.removeEventListener('keydown', pageHandleKeyDown);
    }, [handleClear]);

    return (
        <div
            className={`menu-search-bar${(hasBackButton && isBackButtonShown) ? ' menu-search-bar--has-back-button' : ''}`}
            style={{ top: stickyTop }}
        >
            <div className="menu-search-bar__inner">
                <svg
                    className="menu-search-bar__icon"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#888"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                >
                    <circle cx="11" cy="11" r="8" />
                    <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
                <input
                    type="text"
                    inputMode="search"
                    enterKeyHint="search"
                    placeholder="Search menu..."
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck={false}
                    value={query}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                    className="menu-search-bar__input"
                />
                {query && (
                    <button
                        onClick={handleClear}
                        className="menu-search-bar__clear"
                    >
                        &#x2715;
                    </button>
                )}
            </div>
        </div>
    );
};

export default MenuSearchBar;
